import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Unsubscribe,
  getFirestore,
} from "firebase/firestore";
import { auth } from "../config/firebase";
import { LedgerTransaction } from "../types";

export interface ActivityFeedItem {
  id: string;
  userId: string;
  type: "ISSUE" | "CLAIM" | "REDEEM" | "REFUND" | "REVERSE" | "BLOCK" | "UNBLOCK" | "EXPIRE";
  description: string;
  amount?: number;
  reason?: string;
  timestamp: string;
  createdAt: Date;
}

export interface ActivityFeedState {
  items: ActivityFeedItem[];
  loading: boolean;
  error: string | null;
}

const ACTIVITY_FEED_KEY = "activity_feed_updates";

export function useActivityFeed(maxItems: number = 15) {
  const [state, setState] = useState<ActivityFeedState>({
    items: [],
    loading: true,
    error: null,
  });

  const [unsubscribe, setUnsubscribe] = useState<Unsubscribe | null>(null);

  // Handle cross-tab updates
  const handleStorageChange = useCallback((event: StorageEvent) => {
    if (event.key === ACTIVITY_FEED_KEY && event.newValue) {
      try {
        const newItem = JSON.parse(event.newValue);
        setState((prev) => {
          const itemExists = prev.items.some((i) => i.id === newItem.id);
          if (itemExists) return prev;

          const updated = [newItem, ...prev.items].slice(0, maxItems);
          return { ...prev, items: updated };
        });
      } catch (err) {
        console.error("Failed to parse activity feed update:", err);
      }
    }
  }, [maxItems]);

  // Convert transaction to activity feed item
  const transactionToActivityItem = (
    transaction: LedgerTransaction
  ): ActivityFeedItem => {
    let description = "";

    switch (transaction.type) {
      case "ISSUE":
        description = `Issued wallet item`;
        break;
      case "CLAIM":
        description = `Claimed wallet item`;
        break;
      case "REDEEM":
        description = `Redeemed ${transaction.amount || 0} NOK`;
        break;
      case "REFUND":
        description = `Refunded ${transaction.amount || 0} NOK`;
        break;
      case "REVERSE":
        description = `Reversed transaction`;
        break;
      case "BLOCK":
        description = `Blocked wallet item`;
        break;
      case "UNBLOCK":
        description = `Unblocked wallet item`;
        break;
      case "EXPIRE":
        description = `Wallet item expired`;
        break;
      default:
        description = `${transaction.type} transaction`;
    }

    return {
      id: transaction.txId,
      userId: transaction.userId,
      type: transaction.type,
      description,
      amount: transaction.amount,
      reason: transaction.reason,
      timestamp: transaction.createdAt.toString(),
      createdAt: transaction.createdAt,
    };
  };

  // Setup real-time listener
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setState({
        items: [],
        loading: false,
        error: "No user logged in",
      });
      return;
    }

    try {
      const db = getFirestore();
      const q = query(
        collection(db, "ledger_transactions"),
        where("userId", "==", currentUser.uid),
        orderBy("createdAt", "desc"),
        limit(maxItems)
      );

      const unsubFunc = onSnapshot(
        q,
        (snapshot) => {
          const items: ActivityFeedItem[] = [];

          snapshot.forEach((doc) => {
            const transaction = {
              txId: doc.id,
              ...doc.data(),
            } as LedgerTransaction;

            items.push(transactionToActivityItem(transaction));
          });

          setState({
            items,
            loading: false,
            error: null,
          });

          // Broadcast to other tabs
          if (items.length > 0) {
            try {
              localStorage.setItem(
                ACTIVITY_FEED_KEY,
                JSON.stringify(items[0])
              );
            } catch (err) {
              console.error("Failed to broadcast activity feed update:", err);
            }
          }
        },
        (error) => {
          console.error("Activity feed listener error:", error);
          setState((prev) => ({
            ...prev,
            loading: false,
            error: error.message || "Failed to load activity feed",
          }));
        }
      );

      setUnsubscribe(() => unsubFunc);
    } catch (error: any) {
      console.error("Failed to setup activity feed listener:", error);
      setState({
        items: [],
        loading: false,
        error: error.message || "Failed to setup real-time updates",
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxItems]);

  // Listen for cross-tab updates
  useEffect(() => {
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [handleStorageChange]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    clearError,
  };
}
